
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, CheckCircle2 } from 'lucide-react';

interface ProjectCardProps {
  icon: React.ReactNode;
  title: string;
  catchphrase: string;
  description: string;
  features: string[];
  url: string;
  isNew?: boolean;
}

const ProjectCard: React.FC<ProjectCardProps> = ({
  icon,
  title,
  catchphrase,
  description,
  features,
  url,
  isNew
}) => {
  const { t, i18n } = useTranslation();

  return (
    <div className="group relative flex flex-col p-8 rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden">
      {isNew && (
        <div className="absolute top-0 right-0">
          <div className="bg-[#3B82F6] text-white text-[10px] font-bold px-8 py-1 rotate-45 translate-x-6 translate-y-2 uppercase tracking-widest shadow-sm">
            NEW
          </div>
        </div>
      )}
      
      <div className="flex items-center space-x-4 mb-4">
        <div className="p-3 bg-blue-50 text-[#3B82F6] rounded-xl shadow-sm group-hover:scale-110 transition-transform duration-300">
          {icon}
        </div>
        <div>
          <h3 className="text-2xl font-bold text-[#0F172A]">{title}</h3>
          <p className="text-sm font-semibold text-[#3B82F6]">{catchphrase}</p>
        </div>
      </div>

      <p className="text-[#1E293B]/70 mb-6 leading-relaxed">
        {description}
      </p>

      <ul className="space-y-3 mb-8 flex-grow">
        {features.map((feature, idx) => (
          <li key={idx} className="flex items-start space-x-3 text-sm text-[#1E293B]/80">
            <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <div className="pt-6 border-t border-gray-100 mt-auto flex flex-col sm:flex-row items-center justify-between gap-4">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full sm:w-auto inline-flex items-center justify-center space-x-2 bg-[#0F172A] text-white px-6 py-3 rounded-xl font-medium hover:bg-[#1E293B] hover:shadow-lg transition-all active:scale-95"
        >
          <span>{t('projects.visit')}</span>
          <ExternalLink className="w-4 h-4" />
        </a>
        
        {i18n.language === 'en' && (
          <span className="text-[10px] text-gray-400 italic font-medium">
            {t('projects.aiNote')}
          </span>
        )}
      </div>
    </div>
  );
};

export default ProjectCard;
